
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';
import { ExclamationCircleOutlined } from '@ant-design/icons-vue';
import {
  Tag,
  Input,
  Form,
  Switch,
  Button,
  Checkbox,
  Row,
  Col,
  Modal,
  DatePicker,
  InputNumber,
  message,
} from 'ant-design-vue';
import moment, { Moment } from 'moment';
import { useForm } from '@ant-design-vue/use';
import { Class, IClass, IDistributeForm } from './lib/interface';

export default defineComponent({
  components: {
    aTag: Tag,
    aInput: Input,
    aFormItem: Form.Item,
    aInputNumber: InputNumber,
    aForm: Form,
    aSwitch: Switch,
    aCheckbox: Checkbox,
    aDatePicker: DatePicker,
    ExclamationCircleOutlined,
    Row,
    Col,
    aModal: Modal,
    aButton: Button,
  },
  props: {
    /**
     * 弹窗是否显示
     */
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * 案例id
     */
    caseId: {
      type: String,
      default: '',
    },
    /**
     * 案例名
     */
    caseName: {
      type: String,
      default: '',
    },
    /**
     * 班级列表
     */
    classSource: {
      type: Array as PropType<IClass[]>,
      default: () => [new Class()],
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const classList = ref((cloneDeep(props.classSource) as unknown) as IClass[]);
    const checkedClass = computed(() => {
      try {
        return classList.value.filter((cla) => cla.checked);
      } catch (e) {
        return [];
      }
    });
    const activeClassIdx = ref(0);
    const formState = reactive({
      caseId: 'uuid',
      caseName: '自定义案例名称',
      endTime: null,
      experimentClassReqs: [
        {
          classId: 'string',
          memberReqList: [
            {
              userId: 'string',
            },
          ],
          teacherId: 'string',
        },
      ],
      runStageTotalTime: 20,
      startTime: null,
      viewAnswer: false,
      viewReport: false,
    } as IDistributeForm);
    const formRule = {
      startTime: [{ required: true, message: '请选择开始日期', trigger: 'change' }],
      endTime: [{ required: true, message: '请选择结束日期', trigger: 'change' }],
    };
    const { resetFields } = useForm(formState, formRule);
    const startDisabled = (current: Moment) =>
      (current && current < moment().subtract(1, 'days')) ||
      (current && current > moment(formState.endTime));

    const endDisabled = (current: Moment) =>
      (current && current < moment().subtract(1, 'days')) ||
      (current && current < moment(formState.startTime));
    const handleTagClose = (classId: string) => {
      const cla = classList.value.find((cl) => cl.id === classId);
      if (cla) {
        cla.checked = false;
        cla.classMemberList.forEach((stu) => {
          stu.checked = false;
        });
      }
    };

    const handleClassClick = (cla: IClass, claIdx: number) => {
      activeClassIdx.value = claIdx;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClassChange = (e: any, claIdx: number) => {
      classList.value[claIdx].classMemberList.forEach((stu) => {
        stu.checked = e.target.checked;
      });
      classList.value[claIdx].indeterminate = false;
    };
    const handleStuChange = () => {
      const len = classList.value[activeClassIdx.value].classMemberList?.length as number;
      const checkedLen = classList.value[activeClassIdx.value].classMemberList?.filter(
        (d) => d.checked,
      ).length;
      if (checkedLen === 0) {
        classList.value[activeClassIdx.value].checked = false;
        classList.value[activeClassIdx.value].indeterminate = false;
      }
      if (checkedLen > 0) {
        classList.value[activeClassIdx.value].checked = true;
        if (checkedLen < len) {
          classList.value[activeClassIdx.value].indeterminate = true;
        } else {
          classList.value[activeClassIdx.value].indeterminate = false;
        }
      }
    };
    // eslint-disable-next-line consistent-return
    const handleConfirm = async () => {
      const start = moment(formState.startTime);
      const end = moment(formState.endTime);
      if (!formState.startTime) return message.error('请选择开始时间');
      if (!formState.endTime) return message.error('请选择结束时间');
      if (start >= end || end.valueOf() - start.valueOf() < 1000 * 60 * 1) {
        return message.error('请重新选择结束时间！');
      }
      const data = {} as IDistributeForm;
      Object.assign(data, formState);
      data.startTime = moment(data.startTime).format('YYYY-MM-DD HH:mm:ss');
      data.endTime = moment(data.endTime).format('YYYY-MM-DD HH:mm:ss');
      data.caseId = props.caseId;
      data.caseName = props.caseName;
      data.experimentClassReqs = classList.value
        .filter((cla) => cla.classMemberList.some((stu) => stu.checked))
        .map((cla) => ({
          classId: cla.id,
          teacherId: cla.teacherCode,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          memberReqList: cla.classMemberList
            .filter((user) => user.checked)
            .map((stu) => ({
              userId: stu.userId,
            })),
        }));
      data.experimentClassReqs = data.experimentClassReqs.filter((cla) => cla.memberReqList.length);
      emit('ok', data);
    };
    const handleCancel = () => {
      emit('cancel');
    };
    watch(
      () => props.visible,
      (visible: boolean) => {
        if (!visible) {
          resetFields();
          activeClassIdx.value = 0;
          formState.runStageTotalTime = 20;
          formState.startTime = null;
          formState.endTime = null;
          formState.viewAnswer = false;
          formState.viewReport = false;
          classList.value = (cloneDeep(props.classSource) as unknown) as IClass[];
        }
      },
    );
    return {
      startDisabled,
      endDisabled,
      formState,
      classList,
      activeClassIdx,
      handleTagClose,
      handleClassClick,
      handleClassChange,
      handleStuChange,
      checkedClass,
      handleConfirm,
      handleCancel,
    };
  },
});
